import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  RefreshControl,
  Platform,
  Modal,
  TextInput,
  Image,
} from 'react-native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { Ionicons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { useDialog } from '../contexts';
import { Button } from '../components';
import {
  RootStackParamList,
  PayoutStatus,
  SellerPayout,
  ClubPendingSummary,
  ClubPayout,
} from '../types';
import { adminService, payoutsService } from '../services';
import { Spacing, Typography, Shadows, Colors } from '../constants';

type Props = NativeStackScreenProps<RootStackParamList, 'AdminPayouts'>;

type TabType = 'sellers' | 'clubs';

// Input Modal for capturing reference or failure reason
interface InputModalProps {
  visible: boolean;
  title: string;
  label: string;
  placeholder: string;
  onClose: () => void;
  onSubmit: (value: string) => void;
  submitText?: string;
  variant?: 'primary' | 'danger';
}

// IBAN Input Modal for capturing both IBAN and account holder
interface IbanInputModalProps {
  visible: boolean;
  onClose: () => void;
  onSubmit: (iban: string, accountHolder: string) => void;
}

const InputModal: React.FC<InputModalProps> = ({
  visible,
  title,
  label,
  placeholder,
  onClose,
  onSubmit,
  submitText = 'Submit',
  variant = 'primary',
}) => {
  const { t } = useTranslation();
  const [value, setValue] = useState('');

  const handleSubmit = () => {
    if (value.trim()) {
      onSubmit(value.trim());
      setValue('');
    }
  };

  const handleClose = () => {
    setValue('');
    onClose();
  };

  return (
    <Modal
      visible={visible}
      animationType="fade"
      transparent={true}
      onRequestClose={handleClose}
    >
      <View style={inputModalStyles.overlay}>
        <View style={inputModalStyles.content}>
          <Text style={inputModalStyles.title}>{title}</Text>
          <Text style={inputModalStyles.label}>{label}</Text>
          <TextInput
            style={inputModalStyles.input}
            value={value}
            onChangeText={setValue}
            placeholder={placeholder}
            placeholderTextColor={Colors.textLight}
            autoFocus
          />
          <View style={inputModalStyles.footer}>
            <Button
              title={t('common.buttons.cancel')}
              onPress={handleClose}
              variant="outline"
              style={inputModalStyles.button}
            />
            <Button
              title={submitText}
              onPress={handleSubmit}
              variant="primary"
              style={[
                inputModalStyles.button,
                variant === 'danger' && inputModalStyles.dangerButton,
              ]}
              disabled={!value.trim()}
            />
          </View>
        </View>
      </View>
    </Modal>
  );
};

const inputModalStyles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: Spacing.md,
  },
  content: {
    backgroundColor: Colors.white,
    borderRadius: 12,
    padding: Spacing.lg,
    width: '100%',
    maxWidth: 400,
    ...Shadows.md,
  },
  title: {
    ...Typography.h3,
    color: Colors.text,
    marginBottom: Spacing.md,
  },
  label: {
    ...Typography.body,
    color: Colors.textMedium,
    marginBottom: Spacing.sm,
  },
  input: {
    borderWidth: 1,
    borderColor: Colors.border,
    borderRadius: 8,
    padding: Spacing.md,
    ...Typography.body,
    color: Colors.text,
    marginBottom: Spacing.lg,
  },
  footer: {
    flexDirection: 'row',
    gap: Spacing.sm,
  },
  button: {
    flex: 1,
  },
  dangerButton: {
    backgroundColor: Colors.error,
  },
});

const IbanInputModal: React.FC<IbanInputModalProps> = ({
  visible,
  onClose,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const [iban, setIban] = useState('');
  const [accountHolder, setAccountHolder] = useState('');

  const handleSubmit = () => {
    if (iban.trim() && accountHolder.trim()) {
      onSubmit(iban.trim(), accountHolder.trim());
      setIban('');
      setAccountHolder('');
    }
  };

  const handleClose = () => {
    setIban('');
    setAccountHolder('');
    onClose();
  };

  const isValid = iban.trim().length > 0 && accountHolder.trim().length > 0;

  return (
    <Modal
      visible={visible}
      animationType="fade"
      transparent={true}
      onRequestClose={handleClose}
    >
      <View style={inputModalStyles.overlay}>
        <View style={inputModalStyles.content}>
          <Text style={inputModalStyles.title}>
            {t('screens.adminPayouts.seller.updateIbanTitle')}
          </Text>

          <Text style={inputModalStyles.label}>
            {t('screens.adminPayouts.seller.ibanLabel')}
          </Text>
          <TextInput
            style={inputModalStyles.input}
            value={iban}
            onChangeText={setIban}
            placeholder={t('screens.adminPayouts.seller.ibanPlaceholder')}
            placeholderTextColor={Colors.textLight}
            autoFocus
            autoCapitalize="characters"
          />

          <Text style={inputModalStyles.label}>
            {t('screens.adminPayouts.seller.accountHolderLabel')}
          </Text>
          <TextInput
            style={inputModalStyles.input}
            value={accountHolder}
            onChangeText={setAccountHolder}
            placeholder={t('screens.adminPayouts.seller.accountHolderPlaceholder')}
            placeholderTextColor={Colors.textLight}
            autoCapitalize="words"
          />

          <View style={inputModalStyles.footer}>
            <Button
              title={t('common.buttons.cancel')}
              onPress={handleClose}
              variant="outline"
              style={inputModalStyles.button}
            />
            <Button
              title={t('screens.adminPayouts.seller.updateIban')}
              onPress={handleSubmit}
              variant="primary"
              style={inputModalStyles.button}
              disabled={!isValid}
            />
          </View>
        </View>
      </View>
    </Modal>
  );
};

// Seller Payout Detail Modal
interface SellerPayoutDetailModalProps {
  visible: boolean;
  payout: SellerPayout | null;
  onClose: () => void;
  onMarkPaid: (payoutId: string) => void;
  onMarkFailed: (payoutId: string) => void;
  onUpdateIban: (payoutId: string, transactionId: string) => void;
}

const SellerPayoutDetailModal: React.FC<SellerPayoutDetailModalProps> = ({
  visible,
  payout,
  onClose,
  onMarkPaid,
  onMarkFailed,
  onUpdateIban,
}) => {
  const { t } = useTranslation();

  if (!payout) return null;

  // Access transaction data with proper typing
  const transaction = payout.transaction as (typeof payout.transaction & {
    seller_iban?: string | null;
    seller_account_holder?: string | null;
    item?: { id: string; title: string; image_urls?: string[] } | null;
  }) | undefined;

  const seller = payout.seller;
  const item = transaction?.item;
  const photoUrl = item?.image_urls?.[0];

  const getStatusColor = (status: PayoutStatus) => {
    switch (status) {
      case 'pending':
        return Colors.warning;
      case 'paid':
        return Colors.success;
      case 'failed':
        return Colors.error;
      default:
        return Colors.textMedium;
    }
  };

  return (
    <Modal
      visible={visible}
      animationType="slide"
      transparent={true}
      onRequestClose={onClose}
    >
      <View style={detailModalStyles.overlay}>
        <View style={detailModalStyles.content}>
          {/* Header */}
          <View style={detailModalStyles.header}>
            <Text style={detailModalStyles.headerTitle}>
              {t('screens.adminPayouts.seller.item')}
            </Text>
            <TouchableOpacity onPress={onClose} style={detailModalStyles.closeButton}>
              <Ionicons name="close" size={24} color={Colors.textMedium} />
            </TouchableOpacity>
          </View>

          <ScrollView style={detailModalStyles.body}>
            {/* Status Badge */}
            <View style={detailModalStyles.statusContainer}>
              <View style={[
                detailModalStyles.statusBadge,
                { backgroundColor: getStatusColor(payout.status) + '20' }
              ]}>
                <Text style={[
                  detailModalStyles.statusText,
                  { color: getStatusColor(payout.status) }
                ]}>
                  {t(`screens.adminPayouts.status.${payout.status}`)}
                </Text>
              </View>
            </View>

            {/* Item Info with Thumbnail */}
            {item && (
              <View style={detailModalStyles.section}>
                <Text style={detailModalStyles.sectionTitle}>
                  {t('screens.adminPayouts.seller.item')}
                </Text>
                <View style={detailModalStyles.itemRow}>
                  {photoUrl ? (
                    <Image
                      source={{ uri: photoUrl }}
                      style={detailModalStyles.thumbnail}
                    />
                  ) : (
                    <View style={[detailModalStyles.thumbnail, detailModalStyles.thumbnailPlaceholder]}>
                      <Ionicons name="image-outline" size={24} color={Colors.textLight} />
                    </View>
                  )}
                  <Text style={detailModalStyles.itemTitle}>{item.title}</Text>
                </View>
              </View>
            )}

            {/* Seller Info */}
            <View style={detailModalStyles.section}>
              <Text style={detailModalStyles.sectionTitle}>Seller</Text>
              <View style={detailModalStyles.infoRow}>
                <Ionicons name="person-outline" size={18} color={Colors.textMedium} />
                <Text style={detailModalStyles.infoText}>
                  {seller?.first_name && seller?.last_name
                    ? `${seller.first_name} ${seller.last_name}`
                    : seller?.email || '-'}
                </Text>
              </View>
              <View style={detailModalStyles.infoRow}>
                <Ionicons name="mail-outline" size={18} color={Colors.textMedium} />
                <Text style={detailModalStyles.infoText}>{seller?.email}</Text>
              </View>
            </View>

            {/* Banking Details */}
            <View style={detailModalStyles.section}>
              <Text style={detailModalStyles.sectionTitle}>
                {t('screens.adminPayouts.seller.iban')}
              </Text>
              <View style={detailModalStyles.bankingCard}>
                <View style={detailModalStyles.infoRow}>
                  <Text style={detailModalStyles.infoLabel}>
                    {t('screens.adminPayouts.seller.iban')}:
                  </Text>
                  <Text style={detailModalStyles.infoValue}>
                    {transaction?.seller_iban || '-'}
                  </Text>
                </View>
                <View style={detailModalStyles.infoRow}>
                  <Text style={detailModalStyles.infoLabel}>
                    {t('screens.adminPayouts.seller.accountHolder')}:
                  </Text>
                  <Text style={detailModalStyles.infoValue}>
                    {transaction?.seller_account_holder || '-'}
                  </Text>
                </View>
              </View>
            </View>

            {/* Amount Breakdown */}
            <View style={detailModalStyles.section}>
              <Text style={detailModalStyles.sectionTitle}>
                {t('screens.adminPayouts.seller.amount')}
              </Text>
              <View style={detailModalStyles.amountCard}>
                <View style={detailModalStyles.amountRow}>
                  <Text style={detailModalStyles.amountLabel}>Payout amount</Text>
                  <Text style={detailModalStyles.amountValue}>
                    {'\u20AC'}{payout.amount.toFixed(2)}
                  </Text>
                </View>
                {transaction && (
                  <>
                    <View style={detailModalStyles.divider} />
                    <View style={detailModalStyles.amountRow}>
                      <Text style={detailModalStyles.amountLabelSmall}>Agreed price</Text>
                      <Text style={detailModalStyles.amountValueSmall}>
                        {'\u20AC'}{transaction.agreed_price?.toFixed(2) || '-'}
                      </Text>
                    </View>
                    <View style={detailModalStyles.amountRow}>
                      <Text style={detailModalStyles.amountLabelSmall}>Seller receives</Text>
                      <Text style={detailModalStyles.amountValueSmall}>
                        {'\u20AC'}{transaction.seller_amount?.toFixed(2) || '-'}
                      </Text>
                    </View>
                  </>
                )}
              </View>
            </View>

            {/* Payment Reference (if paid) */}
            {payout.reference && (
              <View style={detailModalStyles.section}>
                <Text style={detailModalStyles.sectionTitle}>
                  {t('screens.adminPayouts.seller.reference')}
                </Text>
                <Text style={detailModalStyles.referenceText}>{payout.reference}</Text>
              </View>
            )}

            {/* Failure Reason (if failed) */}
            {payout.failure_reason && (
              <View style={detailModalStyles.section}>
                <Text style={detailModalStyles.sectionTitle}>
                  {t('screens.adminPayouts.seller.failureReason')}
                </Text>
                <View style={detailModalStyles.failureBox}>
                  <Text style={detailModalStyles.failureText}>{payout.failure_reason}</Text>
                </View>
              </View>
            )}

            {/* Mollie ID (if available) */}
            {transaction?.payment_intent_id && (
              <View style={detailModalStyles.section}>
                <Text style={detailModalStyles.sectionTitle}>
                  {t('screens.adminPayouts.seller.mollieId')}
                </Text>
                <Text style={detailModalStyles.mollieId}>
                  {transaction.payment_intent_id}
                </Text>
              </View>
            )}
          </ScrollView>

          {/* Action Buttons (only for pending status) */}
          {payout.status === 'pending' && (
            <View style={detailModalStyles.footer}>
              <Button
                title={t('screens.adminPayouts.seller.markFailed')}
                onPress={() => onMarkFailed(payout.id)}
                variant="primary"
                style={[detailModalStyles.actionButton, detailModalStyles.dangerButton]}
              />
              <Button
                title={t('screens.adminPayouts.seller.markPaid')}
                onPress={() => onMarkPaid(payout.id)}
                variant="primary"
                style={detailModalStyles.actionButton}
              />
            </View>
          )}

          {/* Update IBAN Button (only for failed status) */}
          {payout.status === 'failed' && transaction?.id && (
            <View style={detailModalStyles.footer}>
              <Button
                title={t('screens.adminPayouts.seller.updateIban')}
                onPress={() => onUpdateIban(payout.id, transaction.id)}
                variant="primary"
                style={detailModalStyles.actionButton}
              />
            </View>
          )}
        </View>
      </View>
    </Modal>
  );
};

const detailModalStyles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  content: {
    backgroundColor: Colors.white,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: '90%',
    ...Shadows.lg,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: Spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: Colors.border,
  },
  headerTitle: {
    ...Typography.h3,
    color: Colors.text,
  },
  closeButton: {
    padding: Spacing.xs,
  },
  body: {
    padding: Spacing.md,
  },
  statusContainer: {
    alignItems: 'flex-start',
    marginBottom: Spacing.md,
  },
  statusBadge: {
    paddingHorizontal: Spacing.md,
    paddingVertical: Spacing.xs,
    borderRadius: 16,
  },
  statusText: {
    ...Typography.caption,
    fontWeight: '600',
    textTransform: 'uppercase',
  },
  section: {
    marginBottom: Spacing.lg,
  },
  sectionTitle: {
    ...Typography.caption,
    color: Colors.textMedium,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: Spacing.sm,
  },
  itemRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.md,
  },
  thumbnail: {
    width: 60,
    height: 60,
    borderRadius: 8,
    backgroundColor: Colors.background,
  },
  thumbnailPlaceholder: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  itemTitle: {
    ...Typography.body,
    color: Colors.text,
    flex: 1,
  },
  infoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.sm,
    marginBottom: Spacing.xs,
  },
  infoText: {
    ...Typography.body,
    color: Colors.text,
  },
  infoLabel: {
    ...Typography.caption,
    color: Colors.textMedium,
    width: 120,
  },
  infoValue: {
    ...Typography.body,
    color: Colors.text,
    flex: 1,
    fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
  },
  bankingCard: {
    backgroundColor: Colors.background,
    padding: Spacing.md,
    borderRadius: 8,
  },
  amountCard: {
    backgroundColor: Colors.background,
    padding: Spacing.md,
    borderRadius: 8,
  },
  amountRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: Spacing.xs,
  },
  amountLabel: {
    ...Typography.body,
    color: Colors.text,
    fontWeight: '600',
  },
  amountValue: {
    ...Typography.h3,
    color: Colors.primary,
  },
  amountLabelSmall: {
    ...Typography.caption,
    color: Colors.textMedium,
  },
  amountValueSmall: {
    ...Typography.body,
    color: Colors.textMedium,
  },
  divider: {
    height: 1,
    backgroundColor: Colors.border,
    marginVertical: Spacing.sm,
  },
  referenceText: {
    ...Typography.body,
    color: Colors.text,
    fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
    backgroundColor: Colors.background,
    padding: Spacing.md,
    borderRadius: 8,
  },
  failureBox: {
    backgroundColor: Colors.error + '10',
    padding: Spacing.md,
    borderRadius: 8,
    borderLeftWidth: 3,
    borderLeftColor: Colors.error,
  },
  failureText: {
    ...Typography.body,
    color: Colors.error,
  },
  mollieId: {
    ...Typography.caption,
    color: Colors.textMedium,
    fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
    backgroundColor: Colors.background,
    padding: Spacing.md,
    borderRadius: 8,
  },
  footer: {
    flexDirection: 'row',
    padding: Spacing.md,
    gap: Spacing.sm,
    borderTopWidth: 1,
    borderTopColor: Colors.border,
  },
  actionButton: {
    flex: 1,
  },
  dangerButton: {
    backgroundColor: Colors.error,
  },
});

export const AdminPayoutsScreen: React.FC<Props> = ({ navigation }) => {
  const { t } = useTranslation();
  const dialog = useDialog();
  const dialogRef = useRef(dialog);
  dialogRef.current = dialog;

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('sellers');
  const [statusFilter, setStatusFilter] = useState<PayoutStatus | 'all'>(
    'pending'
  );

  // Seller payouts data
  const [sellerPayouts, setSellerPayouts] = useState<SellerPayout[]>([]);

  // Club payouts data
  const [clubsPending, setClubsPending] = useState<ClubPendingSummary[]>([]);
  const [clubPayouts, setClubPayouts] = useState<ClubPayout[]>([]);

  // Detail modal state
  const [selectedPayout, setSelectedPayout] = useState<SellerPayout | null>(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);

  // Input modal state for Mark Paid
  const [markPaidModalVisible, setMarkPaidModalVisible] = useState(false);
  const [markPaidPayoutId, setMarkPaidPayoutId] = useState<string | null>(null);

  // Input modal state for Mark Failed
  const [markFailedModalVisible, setMarkFailedModalVisible] = useState(false);
  const [markFailedPayoutId, setMarkFailedPayoutId] = useState<string | null>(null);

  // Input modal state for Update IBAN
  const [updateIbanModalVisible, setUpdateIbanModalVisible] = useState(false);
  const [updateIbanPayoutId, setUpdateIbanPayoutId] = useState<string | null>(null);
  const [updateIbanTransactionId, setUpdateIbanTransactionId] = useState<string | null>(null);

  // Club payout modal states
  const [clubMarkPaidModalVisible, setClubMarkPaidModalVisible] = useState(false);
  const [clubMarkPaidPayoutId, setClubMarkPaidPayoutId] = useState<string | null>(null);
  const [clubMarkFailedModalVisible, setClubMarkFailedModalVisible] = useState(false);
  const [clubMarkFailedPayoutId, setClubMarkFailedPayoutId] = useState<string | null>(null);

  useEffect(() => {
    checkAuthAndLoad();
  }, []);

  const checkAuthAndLoad = async () => {
    const isAdmin = await adminService.isCurrentUserAdmin();
    if (!isAdmin) {
      dialogRef.current.alert(
        t('common.errors.unauthorized'),
        t('common.errors.adminOnly'),
        'error'
      );
      navigation.goBack();
      return;
    }
    await loadData();
  };

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      if (activeTab === 'sellers') {
        const status = statusFilter === 'all' ? undefined : statusFilter;
        const data = await payoutsService.getSellerPayouts(status);
        setSellerPayouts(data);
      } else {
        const [pending, payouts] = await Promise.all([
          payoutsService.getClubsWithPendingPayouts(),
          payoutsService.getClubPayouts(
            undefined,
            statusFilter === 'all' ? undefined : statusFilter
          ),
        ]);
        setClubsPending(pending);
        setClubPayouts(payouts);
      }
    } catch (error) {
      console.error('[AdminPayoutsScreen] Failed to load data:', error);
      const message = error instanceof Error
        ? error.message
        : (error as { message?: string })?.message || 'An unknown error occurred';
      dialogRef.current.alert(
        t('common.errors.error'),
        message,
        'error'
      );
    } finally {
      setLoading(false);
    }
  }, [activeTab, statusFilter, t]);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
  }, [loadData]);

  useEffect(() => {
    if (!loading) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, statusFilter]);

  // Handle selecting a payout to view details
  const handleSelectPayout = (payout: SellerPayout) => {
    setSelectedPayout(payout);
    setDetailModalVisible(true);
  };

  // Handle initiating Mark as Paid flow
  const handleInitiateMarkPaid = (payoutId: string) => {
    setMarkPaidPayoutId(payoutId);
    setDetailModalVisible(false);
    setMarkPaidModalVisible(true);
  };

  // Handle Mark as Paid submission
  const handleMarkPaidSubmit = async (reference: string) => {
    if (!markPaidPayoutId) return;

    try {
      await payoutsService.markSellerPayoutPaid(markPaidPayoutId, reference);
      setMarkPaidModalVisible(false);
      setMarkPaidPayoutId(null);
      setSelectedPayout(null);
      await loadData();
      dialog.alert(
        t('common.success'),
        'Payout marked as paid',
        'success'
      );
    } catch (error) {
      dialog.alert(
        t('common.errors.error'),
        error instanceof Error ? error.message : String(error),
        'error'
      );
    }
  };

  // Handle initiating Mark as Failed flow
  const handleInitiateMarkFailed = (payoutId: string) => {
    setMarkFailedPayoutId(payoutId);
    setDetailModalVisible(false);
    setMarkFailedModalVisible(true);
  };

  // Handle Mark as Failed submission
  const handleMarkFailedSubmit = async (reason: string) => {
    if (!markFailedPayoutId) return;

    try {
      await payoutsService.markSellerPayoutFailed(markFailedPayoutId, reason);
      setMarkFailedModalVisible(false);
      setMarkFailedPayoutId(null);
      setSelectedPayout(null);
      await loadData();
      dialog.alert(
        t('common.success'),
        'Payout marked as failed',
        'success'
      );
    } catch (error) {
      dialog.alert(
        t('common.errors.error'),
        error instanceof Error ? error.message : String(error),
        'error'
      );
    }
  };

  // Handle initiating Update IBAN flow
  const handleInitiateUpdateIban = (payoutId: string, transactionId: string) => {
    setUpdateIbanPayoutId(payoutId);
    setUpdateIbanTransactionId(transactionId);
    setDetailModalVisible(false);
    setUpdateIbanModalVisible(true);
  };

  // Handle Update IBAN submission
  const handleUpdateIbanSubmit = async (iban: string, accountHolder: string) => {
    if (!updateIbanPayoutId || !updateIbanTransactionId) return;

    try {
      await payoutsService.updateSellerPayoutIban(
        updateIbanPayoutId,
        updateIbanTransactionId,
        iban,
        accountHolder
      );
      setUpdateIbanModalVisible(false);
      setUpdateIbanPayoutId(null);
      setUpdateIbanTransactionId(null);
      setSelectedPayout(null);
      await loadData();
      dialog.alert(
        t('common.success'),
        'IBAN updated and payout reset to pending',
        'success'
      );
    } catch (error) {
      dialog.alert(
        t('common.errors.error'),
        error instanceof Error ? error.message : String(error),
        'error'
      );
    }
  };

  // Handle Create Club Payout
  const handleCreateClubPayout = async (
    clubId: string,
    clubName: string,
    amount: number,
    count: number
  ) => {
    const confirmed = await dialog.confirm(
      t('screens.adminPayouts.club.createPayout'),
      t('screens.adminPayouts.club.confirmCreate', {
        amount: `\u20AC${amount.toFixed(2)}`,
        count,
      })
    );
    if (!confirmed) return;

    try {
      await payoutsService.createClubPayout(clubId);
      await loadData();
      dialog.alert(t('common.success'), `Payout created for ${clubName}`, 'success');
    } catch (error) {
      dialog.alert(
        t('common.errors.error'),
        error instanceof Error ? error.message : String(error),
        'error'
      );
    }
  };

  // Handle initiating Club Mark as Paid flow
  const handleInitiateClubMarkPaid = (payoutId: string) => {
    setClubMarkPaidPayoutId(payoutId);
    setClubMarkPaidModalVisible(true);
  };

  // Handle Club Mark as Paid submission
  const handleClubMarkPaidSubmit = async (reference: string) => {
    if (!clubMarkPaidPayoutId) return;

    try {
      await payoutsService.markClubPayoutPaid(clubMarkPaidPayoutId, reference);
      setClubMarkPaidModalVisible(false);
      setClubMarkPaidPayoutId(null);
      await loadData();
      dialog.alert(t('common.success'), 'Club payout marked as paid', 'success');
    } catch (error) {
      dialog.alert(
        t('common.errors.error'),
        error instanceof Error ? error.message : String(error),
        'error'
      );
    }
  };

  // Handle initiating Club Mark as Failed flow
  const handleInitiateClubMarkFailed = (payoutId: string) => {
    setClubMarkFailedPayoutId(payoutId);
    setClubMarkFailedModalVisible(true);
  };

  // Handle Club Mark as Failed submission
  const handleClubMarkFailedSubmit = async (reason: string) => {
    if (!clubMarkFailedPayoutId) return;

    try {
      await payoutsService.markClubPayoutFailed(clubMarkFailedPayoutId, reason);
      setClubMarkFailedModalVisible(false);
      setClubMarkFailedPayoutId(null);
      await loadData();
      dialog.alert(t('common.success'), 'Club payout marked as failed', 'success');
    } catch (error) {
      dialog.alert(
        t('common.errors.error'),
        error instanceof Error ? error.message : String(error),
        'error'
      );
    }
  };

  if (loading) {
    return (
      <View style={styles.centered}>
        <ActivityIndicator size="large" color={Colors.primary} />
      </View>
    );
  }

  return (
    <View style={styles.wrapper}>
      {/* Header */}
      <View style={[styles.headerContainer, Platform.OS === 'web' && styles.headerContainerWeb]}>
        <View style={styles.headerContent}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.canGoBack() ? navigation.goBack() : navigation.navigate('AdminDashboard')}
          >
            <Ionicons name="arrow-back" size={24} color={Colors.primary} />
            <Text style={styles.backText}>{t('common.buttons.back')}</Text>
          </TouchableOpacity>
          <Text style={styles.headerTitle}>
            {t('screens.adminPayouts.title')}
          </Text>
        </View>
      </View>

      {/* Tabs */}
      <View style={[styles.tabContainerOuter, Platform.OS === 'web' && styles.tabContainerOuterWeb]}>
        <View style={styles.tabContainerInner}>
          <TouchableOpacity
            style={[styles.tab, activeTab === 'sellers' && styles.tabActive]}
            onPress={() => setActiveTab('sellers')}
          >
            <Text
              style={[
                styles.tabText,
                activeTab === 'sellers' && styles.tabTextActive,
              ]}
            >
              {t('screens.adminPayouts.tabs.sellers')}
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.tab, activeTab === 'clubs' && styles.tabActive]}
            onPress={() => setActiveTab('clubs')}
          >
            <Text
              style={[
                styles.tabText,
                activeTab === 'clubs' && styles.tabTextActive,
              ]}
            >
              {t('screens.adminPayouts.tabs.clubs')}
            </Text>
          </TouchableOpacity>
        </View>
      </View>

      {/* Status Filter */}
      <View style={[styles.filterContainerOuter, Platform.OS === 'web' && styles.filterContainerOuterWeb]}>
        <View style={styles.filterContainerInner}>
          {(['all', 'pending', 'paid', 'failed'] as const).map((status) => (
            <TouchableOpacity
              key={status}
              style={[
                styles.filterChip,
                statusFilter === status && styles.filterChipActive,
              ]}
              onPress={() => setStatusFilter(status)}
            >
              <Text
                style={[
                  styles.filterText,
                  statusFilter === status && styles.filterTextActive,
                ]}
              >
                {t(`screens.adminPayouts.filters.${status}`)}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>

      {/* Content */}
      <ScrollView
        style={styles.container}
        contentContainerStyle={styles.scrollContent}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
      >
        <View style={styles.contentContainer}>
          {activeTab === 'sellers' ? (
            <SellerPayoutsList
              payouts={sellerPayouts}
              onRefresh={loadData}
              onSelectPayout={handleSelectPayout}
            />
          ) : (
            <ClubPayoutsTab
              pending={clubsPending}
              payouts={clubPayouts}
              onRefresh={loadData}
              onCreatePayout={handleCreateClubPayout}
              onMarkPaid={handleInitiateClubMarkPaid}
              onMarkFailed={handleInitiateClubMarkFailed}
            />
          )}
        </View>
      </ScrollView>

      {/* Seller Payout Detail Modal */}
      <SellerPayoutDetailModal
        visible={detailModalVisible}
        payout={selectedPayout}
        onClose={() => {
          setDetailModalVisible(false);
          setSelectedPayout(null);
        }}
        onMarkPaid={handleInitiateMarkPaid}
        onMarkFailed={handleInitiateMarkFailed}
        onUpdateIban={handleInitiateUpdateIban}
      />

      {/* Mark Paid Input Modal */}
      <InputModal
        visible={markPaidModalVisible}
        title={t('screens.adminPayouts.seller.markPaid')}
        label={t('screens.adminPayouts.seller.reference')}
        placeholder={t('screens.adminPayouts.seller.referencePlaceholder')}
        onClose={() => {
          setMarkPaidModalVisible(false);
          setMarkPaidPayoutId(null);
        }}
        onSubmit={handleMarkPaidSubmit}
        submitText={t('screens.adminPayouts.seller.markPaid')}
        variant="primary"
      />

      {/* Mark Failed Input Modal */}
      <InputModal
        visible={markFailedModalVisible}
        title={t('screens.adminPayouts.seller.markFailed')}
        label={t('screens.adminPayouts.seller.failureReason')}
        placeholder={t('screens.adminPayouts.seller.failureReasonPlaceholder')}
        onClose={() => {
          setMarkFailedModalVisible(false);
          setMarkFailedPayoutId(null);
        }}
        onSubmit={handleMarkFailedSubmit}
        submitText={t('screens.adminPayouts.seller.markFailed')}
        variant="danger"
      />

      {/* Update IBAN Input Modal */}
      <IbanInputModal
        visible={updateIbanModalVisible}
        onClose={() => {
          setUpdateIbanModalVisible(false);
          setUpdateIbanPayoutId(null);
          setUpdateIbanTransactionId(null);
        }}
        onSubmit={handleUpdateIbanSubmit}
      />

      {/* Club Mark Paid Input Modal */}
      <InputModal
        visible={clubMarkPaidModalVisible}
        title={t('screens.adminPayouts.seller.markPaid')}
        label={t('screens.adminPayouts.seller.reference')}
        placeholder={t('screens.adminPayouts.seller.referencePlaceholder')}
        onClose={() => {
          setClubMarkPaidModalVisible(false);
          setClubMarkPaidPayoutId(null);
        }}
        onSubmit={handleClubMarkPaidSubmit}
        submitText={t('screens.adminPayouts.seller.markPaid')}
        variant="primary"
      />

      {/* Club Mark Failed Input Modal */}
      <InputModal
        visible={clubMarkFailedModalVisible}
        title={t('screens.adminPayouts.seller.markFailed')}
        label={t('screens.adminPayouts.seller.failureReason')}
        placeholder={t('screens.adminPayouts.seller.failureReasonPlaceholder')}
        onClose={() => {
          setClubMarkFailedModalVisible(false);
          setClubMarkFailedPayoutId(null);
        }}
        onSubmit={handleClubMarkFailedSubmit}
        submitText={t('screens.adminPayouts.seller.markFailed')}
        variant="danger"
      />
    </View>
  );
};

// Seller Payouts List Component
const SellerPayoutsList: React.FC<{
  payouts: SellerPayout[];
  onRefresh: () => void;
  onSelectPayout: (payout: SellerPayout) => void;
}> = ({ payouts, onSelectPayout }) => {
  const { t } = useTranslation();

  if (payouts.length === 0) {
    return (
      <Text style={styles.emptyText}>
        {t('screens.adminPayouts.seller.emptyState')}
      </Text>
    );
  }

  const getStatusColor = (status: PayoutStatus) => {
    switch (status) {
      case 'pending':
        return Colors.warning;
      case 'paid':
        return Colors.success;
      case 'failed':
        return Colors.error;
      default:
        return Colors.textMedium;
    }
  };

  return (
    <View>
      {payouts.map((payout) => (
        <TouchableOpacity
          key={payout.id}
          style={styles.payoutRow}
          onPress={() => onSelectPayout(payout)}
          activeOpacity={0.7}
        >
          <View style={styles.payoutMainInfo}>
            <Text style={styles.payoutName}>
              {payout.seller?.email || 'Unknown'}
            </Text>
            <Text style={styles.payoutItemTitle} numberOfLines={1}>
              {(payout.transaction as { item?: { title: string } })?.item?.title || '-'}
            </Text>
          </View>
          <View style={styles.payoutRightInfo}>
            <Text style={styles.payoutAmount}>
              {'\u20AC'}{payout.amount.toFixed(2)}
            </Text>
            <View style={[
              styles.statusBadge,
              { backgroundColor: getStatusColor(payout.status) + '20' }
            ]}>
              <Text style={[
                styles.statusBadgeText,
                { color: getStatusColor(payout.status) }
              ]}>
                {t(`screens.adminPayouts.status.${payout.status}`)}
              </Text>
            </View>
          </View>
          <Ionicons name="chevron-forward" size={20} color={Colors.textLight} />
        </TouchableOpacity>
      ))}
    </View>
  );
};

const ClubPayoutsTab: React.FC<{
  pending: ClubPendingSummary[];
  payouts: ClubPayout[];
  onRefresh: () => void;
  onCreatePayout: (clubId: string, clubName: string, amount: number, count: number) => void;
  onMarkPaid: (payoutId: string) => void;
  onMarkFailed: (payoutId: string) => void;
}> = ({ pending, payouts, onCreatePayout, onMarkPaid, onMarkFailed }) => {
  const { t } = useTranslation();

  const getStatusColor = (status: PayoutStatus) => {
    switch (status) {
      case 'pending':
        return Colors.warning;
      case 'paid':
        return Colors.success;
      case 'failed':
        return Colors.error;
      default:
        return Colors.textMedium;
    }
  };

  const hasPending = pending.length > 0;
  const hasPayouts = payouts.length > 0;

  if (!hasPending && !hasPayouts) {
    return (
      <Text style={styles.emptyText}>
        {t('screens.adminPayouts.club.emptyState')}
      </Text>
    );
  }

  return (
    <View>
      {/* Pending Clubs Section */}
      {hasPending && (
        <View style={clubStyles.section}>
          <Text style={clubStyles.sectionTitle}>
            {t('screens.adminPayouts.club.pendingAmount')}
          </Text>
          {pending.map((club) => (
            <View key={club.club_id} style={clubStyles.pendingCard}>
              <View style={clubStyles.pendingInfo}>
                <Text style={clubStyles.clubName}>{club.club_name}</Text>
                <View style={clubStyles.pendingDetails}>
                  <Text style={clubStyles.amount}>
                    {'\u20AC'}{club.pending_amount.toFixed(2)}
                  </Text>
                  <Text style={clubStyles.transactionCount}>
                    {club.transaction_count} {t('screens.adminPayouts.club.transactions')}
                  </Text>
                </View>
              </View>
              <TouchableOpacity
                style={clubStyles.createButton}
                onPress={() =>
                  onCreatePayout(
                    club.club_id,
                    club.club_name,
                    club.pending_amount,
                    club.transaction_count
                  )
                }
              >
                <Ionicons name="add-circle" size={20} color={Colors.white} />
                <Text style={clubStyles.createButtonText}>
                  {t('screens.adminPayouts.club.createPayout')}
                </Text>
              </TouchableOpacity>
            </View>
          ))}
        </View>
      )}

      {/* Past Payouts Section */}
      {hasPayouts && (
        <View style={clubStyles.section}>
          <Text style={clubStyles.sectionTitle}>
            {t('screens.adminPayouts.club.pastPayouts')}
          </Text>
          {payouts.map((payout) => {
            const clubData = payout.club as { id: string; name: string } | undefined;
            return (
              <View key={payout.id} style={clubStyles.payoutCard}>
                <View style={clubStyles.payoutHeader}>
                  <Text style={clubStyles.clubName}>
                    {clubData?.name || 'Unknown Club'}
                  </Text>
                  <View
                    style={[
                      clubStyles.statusBadge,
                      { backgroundColor: getStatusColor(payout.status) + '20' },
                    ]}
                  >
                    <Text
                      style={[
                        clubStyles.statusText,
                        { color: getStatusColor(payout.status) },
                      ]}
                    >
                      {t(`screens.adminPayouts.status.${payout.status}`)}
                    </Text>
                  </View>
                </View>
                <View style={clubStyles.payoutDetails}>
                  <Text style={clubStyles.amount}>
                    {'\u20AC'}{payout.amount.toFixed(2)}
                  </Text>
                  <Text style={clubStyles.date}>
                    {new Date(payout.created_at).toLocaleDateString()}
                  </Text>
                </View>

                {/* Reference (if paid) */}
                {payout.reference && (
                  <Text style={clubStyles.reference}>
                    Ref: {payout.reference}
                  </Text>
                )}

                {/* Failure reason (if failed) */}
                {payout.failure_reason && (
                  <View style={clubStyles.failureBox}>
                    <Text style={clubStyles.failureText}>
                      {payout.failure_reason}
                    </Text>
                  </View>
                )}

                {/* Action buttons for pending payouts */}
                {payout.status === 'pending' && (
                  <View style={clubStyles.actionButtons}>
                    <TouchableOpacity
                      style={[clubStyles.actionButton, clubStyles.failButton]}
                      onPress={() => onMarkFailed(payout.id)}
                    >
                      <Ionicons name="close-circle" size={16} color={Colors.white} />
                      <Text style={clubStyles.actionButtonText}>
                        {t('screens.adminPayouts.seller.markFailed')}
                      </Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[clubStyles.actionButton, clubStyles.paidButton]}
                      onPress={() => onMarkPaid(payout.id)}
                    >
                      <Ionicons name="checkmark-circle" size={16} color={Colors.white} />
                      <Text style={clubStyles.actionButtonText}>
                        {t('screens.adminPayouts.seller.markPaid')}
                      </Text>
                    </TouchableOpacity>
                  </View>
                )}
              </View>
            );
          })}
        </View>
      )}
    </View>
  );
};

const clubStyles = StyleSheet.create({
  section: {
    marginBottom: Spacing.lg,
  },
  sectionTitle: {
    ...Typography.caption,
    color: Colors.textMedium,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: Spacing.sm,
  },
  pendingCard: {
    backgroundColor: Colors.white,
    borderRadius: 12,
    padding: Spacing.md,
    marginBottom: Spacing.sm,
    ...Shadows.sm,
  },
  pendingInfo: {
    marginBottom: Spacing.md,
  },
  clubName: {
    ...Typography.body,
    color: Colors.text,
    fontWeight: '600',
    marginBottom: Spacing.xs,
  },
  pendingDetails: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.md,
  },
  amount: {
    ...Typography.h3,
    color: Colors.primary,
  },
  transactionCount: {
    ...Typography.caption,
    color: Colors.textMedium,
  },
  createButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: Colors.primary,
    borderRadius: 8,
    paddingVertical: Spacing.sm,
    paddingHorizontal: Spacing.md,
    gap: Spacing.xs,
  },
  createButtonText: {
    ...Typography.body,
    color: Colors.white,
    fontWeight: '600',
  },
  payoutCard: {
    backgroundColor: Colors.white,
    borderRadius: 12,
    padding: Spacing.md,
    marginBottom: Spacing.sm,
    ...Shadows.sm,
  },
  payoutHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: Spacing.sm,
  },
  statusBadge: {
    paddingHorizontal: Spacing.sm,
    paddingVertical: 2,
    borderRadius: 10,
  },
  statusText: {
    ...Typography.caption,
    fontSize: 10,
    fontWeight: '600',
    textTransform: 'uppercase',
  },
  payoutDetails: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  date: {
    ...Typography.caption,
    color: Colors.textMedium,
  },
  reference: {
    ...Typography.caption,
    color: Colors.textMedium,
    marginTop: Spacing.sm,
    fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
  },
  failureBox: {
    backgroundColor: Colors.error + '10',
    padding: Spacing.sm,
    borderRadius: 8,
    borderLeftWidth: 3,
    borderLeftColor: Colors.error,
    marginTop: Spacing.sm,
  },
  failureText: {
    ...Typography.caption,
    color: Colors.error,
  },
  actionButtons: {
    flexDirection: 'row',
    gap: Spacing.sm,
    marginTop: Spacing.md,
  },
  actionButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: Spacing.sm,
    borderRadius: 8,
    gap: Spacing.xs,
  },
  paidButton: {
    backgroundColor: Colors.success,
  },
  failButton: {
    backgroundColor: Colors.error,
  },
  actionButtonText: {
    ...Typography.caption,
    color: Colors.white,
    fontWeight: '600',
  },
});

const styles = StyleSheet.create({
  wrapper: {
    flex: 1,
    backgroundColor: Colors.background,
  },
  centered: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: Colors.background,
  },
  headerContainer: {
    padding: Spacing.md,
    paddingTop: Platform.OS === 'web' ? Spacing.md : 50,
    backgroundColor: Colors.white,
    borderBottomWidth: 1,
    borderBottomColor: Colors.border,
  },
  headerContainerWeb: {
    alignItems: 'center' as const,
  },
  headerContent: {
    width: '100%',
    maxWidth: 1200,
  },
  backButton: {
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    marginBottom: Spacing.xs,
  },
  backText: {
    fontSize: 16,
    color: Colors.primary,
    marginLeft: 4,
  },
  headerTitle: {
    ...Typography.h3,
    color: Colors.text,
  },
  tabContainerOuter: {
    backgroundColor: Colors.white,
    borderBottomWidth: 1,
    borderBottomColor: Colors.border,
  },
  tabContainerOuterWeb: {
    alignItems: 'center' as const,
  },
  tabContainerInner: {
    flexDirection: 'row' as const,
    width: '100%',
    maxWidth: 1200,
  },
  tab: {
    flex: 1,
    paddingVertical: Spacing.md,
    alignItems: 'center',
  },
  tabActive: {
    borderBottomWidth: 2,
    borderBottomColor: Colors.primary,
  },
  tabText: {
    ...Typography.body,
    color: Colors.textMedium,
  },
  tabTextActive: {
    color: Colors.primary,
    fontWeight: '600',
  },
  filterContainerOuter: {
    backgroundColor: Colors.white,
  },
  filterContainerOuterWeb: {
    alignItems: 'center' as const,
  },
  filterContainerInner: {
    flexDirection: 'row' as const,
    padding: Spacing.sm,
    gap: Spacing.xs,
    width: '100%',
    maxWidth: 1200,
  },
  filterChip: {
    paddingHorizontal: Spacing.md,
    paddingVertical: Spacing.xs,
    borderRadius: 16,
    backgroundColor: Colors.background,
  },
  filterChipActive: {
    backgroundColor: Colors.primary,
  },
  filterText: {
    ...Typography.caption,
    color: Colors.textMedium,
  },
  filterTextActive: {
    color: Colors.white,
  },
  container: {
    flex: 1,
  },
  scrollContent: {
    ...(Platform.OS === 'web' && {
      alignItems: 'center' as const,
    }),
  },
  contentContainer: {
    width: '100%',
    padding: Spacing.md,
    ...(Platform.OS === 'web' && {
      maxWidth: 1200,
    }),
  },
  emptyText: {
    ...Typography.body,
    color: Colors.textMedium,
    textAlign: 'center',
    marginTop: Spacing.xl,
  },
  payoutRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: Spacing.md,
    backgroundColor: Colors.white,
    borderRadius: 8,
    marginBottom: Spacing.sm,
    ...Shadows.sm,
  },
  payoutMainInfo: {
    flex: 1,
    marginRight: Spacing.sm,
  },
  payoutName: {
    ...Typography.body,
    color: Colors.text,
    fontWeight: '500',
  },
  payoutItemTitle: {
    ...Typography.caption,
    color: Colors.textMedium,
    marginTop: 2,
  },
  payoutRightInfo: {
    alignItems: 'flex-end',
    marginRight: Spacing.sm,
  },
  payoutAmount: {
    ...Typography.body,
    color: Colors.primary,
    fontWeight: '600',
  },
  payoutStatus: {
    ...Typography.caption,
    color: Colors.textMedium,
  },
  statusBadge: {
    paddingHorizontal: Spacing.sm,
    paddingVertical: 2,
    borderRadius: 10,
    marginTop: 4,
  },
  statusBadgeText: {
    ...Typography.caption,
    fontSize: 10,
    fontWeight: '600',
    textTransform: 'uppercase',
  },
});
